import { http, HttpResponse, delay } from 'msw';
import { DOCUMENT_ENDPOINTS } from '../constants';
import { MOCK_DOCUMENTS } from './documents.mock';
import type { Document } from '../models';

let documents = [...MOCK_DOCUMENTS];

function toEnvelope<T>(data: T) {
  return {
    status: true,
    status_code: 200,
    message: 'ok',
    data,
    error: null,
  };
}

export const documentsHandlers = [
  http.get(DOCUMENT_ENDPOINTS.LIST, async ({ request }) => {
    await delay(500);
    const url = new URL(request.url);
    if (url.searchParams.get('error') === '1') {
      return HttpResponse.json({ message: 'Failed to load documents' }, { status: 500 });
    }

    if (url.searchParams.get('empty') === '1') {
      return HttpResponse.json(toEnvelope({ pdfs: [] }));
    }

    return HttpResponse.json(toEnvelope({ pdfs: documents }));
  }),

  /** GET /admin/pdfs/{id}/file — must match authenticated blob download in the app */
  http.get('*/admin/pdfs/:pdfId/file', async () => {
    await delay(100);
    const blob = new Blob(['%PDF-1.4\n'], { type: 'application/pdf' });
    return new HttpResponse(blob, {
      headers: {
        'Content-Type': 'application/pdf',
        'Content-Disposition': 'attachment; filename="document.pdf"',
      },
    });
  }),

  http.post(DOCUMENT_ENDPOINTS.CREATE, async ({ request }) => {
    await delay(800);
    const url = new URL(request.url);
    if (url.searchParams.get('error') === '1') {
      return HttpResponse.json({ message: 'Upload failed' }, { status: 500 });
    }

    const formData = await request.formData();
    const file = formData.get('file') as File | null;
    const public_slug = String(formData.get('public_slug') ?? '').trim();
    const title = String(formData.get('title') ?? '').trim();

    if (!file || !public_slug || !title) {
      return HttpResponse.json(
        { message: 'file, public_slug, and title are required' },
        { status: 400 }
      );
    }

    const existing = documents.findIndex(d => d.publicSlug === public_slug);
    const next: Document = {
      id: existing >= 0 ? documents[existing].id : `doc-${Date.now()}`,
      title,
      name: title,
      publicSlug: public_slug,
      currentFileName: file.name,
      uploadedAt: new Date().toISOString(),
      uploadedBy: 'Current User',
      fileSize: file.size,
    };

    if (existing >= 0) {
      documents[existing] = next;
    } else {
      documents = [next, ...documents];
    }

    return HttpResponse.json(toEnvelope(next));
  }),

  http.delete(`${DOCUMENT_ENDPOINTS.LIST}/:pdfId`, async ({ params }) => {
    await delay(200);
    const id = String(params.pdfId ?? '');
    const before = documents.length;
    documents = documents.filter(d => d.id !== id);
    if (documents.length === before) {
      return HttpResponse.json({ message: 'PDF not found' }, { status: 404 });
    }
    return HttpResponse.json(toEnvelope(null));
  }),
];
